import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  defaultSummaryStatsSettings,
  fetchSummaryStatsSettings,
  mergeSettings,
  resolveSettingsScenarioId,
  saveSummaryStatsSettings,
  summaryStatsSettingsKey,
  type SummaryStatsSettings,
} from '@/core/cache/summaryStatsSettings'

/**
 * The stat cards' server-persisted settings, for React (Phase 1I).
 *
 * The key, the defaults, the response shapes and the requests live in
 * `core/cache/summaryStatsSettings.ts`, which the Vue composable also uses — so
 * the Customize Map modal (still Vue) and the React cards read and write ONE
 * cache entry. Options match it too: a different `staleTime` here would mean
 * the two disagree about when the settings are stale.
 *
 * The server owns `activeCards`: it decides which cards the bar shows.
 */
export function useSummaryStatsSettings(scenarioId: number | null, workspaceId: number) {
  const queryClient = useQueryClient()
  const resolved = resolveSettingsScenarioId(scenarioId)
  const queryKey = summaryStatsSettingsKey(resolved, workspaceId)

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchSummaryStatsSettings(resolved, workspaceId),
    enabled: resolved !== null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnMount: true,
  })

  const save = useMutation({
    mutationFn: (settings: Partial<SummaryStatsSettings>) =>
      saveSummaryStatsSettings(resolved, settings, workspaceId),
    onSuccess: (saved) => queryClient.setQueryData(queryKey, saved),
  })

  return {
    settings: data || defaultSummaryStatsSettings(),
    isLoading,
    /**
     * Write into the shared cache before the round trip, then persist. This is
     * what keeps the Customize Map modal and the bar in agreement; `activeCards`
     * merges, everything else replaces.
     */
    saveSettings: async (partial: Partial<SummaryStatsSettings>) => {
      const current = queryClient.getQueryData<SummaryStatsSettings>(queryKey)
      const merged = mergeSettings(current, partial)
      queryClient.setQueryData(queryKey, merged)
      return save.mutateAsync(merged)
    },
  }
}
